import { Component, type ErrorInfo, type ReactNode } from 'react';
import { RotateCcw, TriangleAlert } from 'lucide-react';
import { estDuBruit, signalerIncident } from '@/shared/incidents/incidents';

/**
 * Un déploiement a eu lieu pendant que l'onglet était ouvert : l'ancien morceau
 * de l'application n'existe plus (Chrome, Firefox, Safari le disent chacun à
 * leur façon). Recharger suffit.
 */
function versionPerimee(message: string): boolean {
  return /dynamically imported module|Importing a module script failed|Failed to fetch|not a valid JavaScript MIME type|error loading dynamically/i.test(
    message
  );
}

const CLE_RECHARGEMENT = 'vla-rechargement-version';

/**
 * Si un écran plante, le reste de l'application tient : la navigation reste là,
 * l'écran dit ce qui s'est passé et propose de recharger. Une page blanche, sur
 * un chantier, c'est un téléphone qu'on range.
 */
export class FiletErreur extends Component<{ children: ReactNode }, { erreur: Error | null }> {
  override state: { erreur: Error | null } = { erreur: null };

  static getDerivedStateFromError(erreur: Error) {
    return { erreur };
  }

  override componentDidUpdate() {
    /* Une version périmée : on recharge tout seul, une fois. Si l'erreur revient
       aussitôt, on s'arrête et on laisse le bouton (pas de boucle). */
    const erreur = this.state.erreur;
    /* Hors ligne, recharger ne servirait à rien : le bouton reste. */
    if (!erreur || !versionPerimee(erreur.message) || !navigator.onLine) return;
    try {
      const dernier = Number(sessionStorage.getItem(CLE_RECHARGEMENT) ?? 0);
      if (Date.now() - dernier < 30_000) return;
      sessionStorage.setItem(CLE_RECHARGEMENT, String(Date.now()));
    } catch {
      return;
    }
    window.location.reload();
  }

  override componentDidCatch(erreur: Error, info: ErrorInfo) {
    console.error('Écran interrompu', erreur, info.componentStack);
    /* Une version périmée ou un réseau absent ne sont pas des pannes à signaler. */
    if (estDuBruit(erreur.message, erreur.stack)) return;
    signalerIncident({
      message: erreur.message,
      detail: [erreur.stack, info.componentStack ? `Composants :${info.componentStack}` : '']
        .filter(Boolean)
        .join('\n\n'),
      page: window.location.pathname,
    });
  }

  override render() {
    if (!this.state.erreur) return this.props.children;
    /* Un déploiement a eu lieu pendant que l'onglet était ouvert : l'ancien
       morceau de l'application n'existe plus. Recharger suffit. */
    const perimee = versionPerimee(this.state.erreur.message);
    return (
      <div className="mx-auto mt-10 flex max-w-md flex-col items-center gap-3 rounded-2xl border border-border bg-surface p-8 text-center shadow-card">
        <span className="grid size-14 place-items-center rounded-2xl bg-warning-bg text-warning">
          <TriangleAlert className="size-7" aria-hidden />
        </span>
        <h2 className="text-lg font-semibold">
          {perimee ? 'Une nouvelle version est disponible' : 'Cet écran a rencontré un problème'}
        </h2>
        <p className="text-[13px] text-muted">
          {perimee
            ? 'Rechargez la page pour la prendre. Rien de ce que vous avez enregistré n’est perdu.'
            : 'Rien de ce que vous avez enregistré n’est perdu. Rechargez la page ; si cela se reproduit, prévenez le bureau.'}
        </p>
        <button
          type="button"
          onClick={() => {
            window.location.reload();
          }}
          className="mt-2 inline-flex h-10 items-center gap-2 rounded-[10px] bg-brand px-4 text-[13px] font-medium text-white shadow-glow"
        >
          <RotateCcw className="size-4" aria-hidden />
          Recharger
        </button>
      </div>
    );
  }
}
